// Pure rules behind the store detail page (WEB_PLAN.md §5.6), ported from RecordDetailView.swift (hero stats),
// ActivityRow.swift (tint, verification pill, by-line, date line), RecordInfoTab.swift (address lines, phone / mail /
// web links, custom field rows) and RecordFilesTab.swift (photos vs documents). Kept out of the components so the
// wording and the edge cases are unit-tested.

import { USER_ROLE_TITLES, type ActivityType, type DistanceUnit } from '@/contracts/enums';
import { fieldValueDate, fieldValueDisplay, type FieldValue } from '@/contracts/fieldValue';
import type {
  ActivityPayload,
  AddressPayload,
  AttachmentPayload,
  CustomFieldPayload,
  UserPayload,
  VisitResultPayload,
} from '@/contracts/models';
import { sameId } from '@/contracts/uuid';
import { parseWireDate } from '@/contracts/wireDate';
import type { DataItem } from '@/data/lists';
import { isSameDay, startOfDayOffset } from '@/domain/calendar';
import { effectiveDate, isVerified } from '@/domain/derived';
import { formatDayDescription, formatMoney, formatShortDateTime, formatShortDistance, formatShortTime } from '@/domain/format';
import { isOverdue } from '@/domain/records';
import { fullName, lookup } from '@/domain/util';

// MARK: - Activity rows

/** `ActivityType.tint` (Components+Store.swift). */
export const ACTIVITY_TYPE_COLORS: Readonly<Record<ActivityType, string>> = {
  visit: '#F96A3C',
  call: '#29A85C',
  email: '#2673E6',
  text: '#009E9E',
  note: '#8C919E',
  meeting: '#7D4DBF',
  task: '#102A5E',
  stageChange: '#F5B400',
  assignment: '#102A5E',
  created: '#29A85C',
  photo: '#7D4DBF',
  document: '#8C919E',
};

/** ActivityRow's badge color: a visit takes its result's color, everything else its type tint. */
export function activityTint(
  activity: Pick<ActivityPayload, 'type' | 'resultId'>,
  visitResults: ReadonlyMap<string, Pick<VisitResultPayload, 'colorHex'>>,
): string {
  if (activity.type === 'visit') {
    const result = lookup(visitResults, activity.resultId);
    if (result) return result.colorHex;
  }
  return ACTIVITY_TYPE_COLORS[activity.type] ?? '#8C919E';
}

/** "By Jane Doe (Manager)"; reps get no role; "By Unassigned" when the author is unknown (ActivityRow.byLine). */
export function activityByline(user: Pick<UserPayload, 'firstName' | 'lastName' | 'role'> | undefined): string {
  if (!user) return 'By Unassigned';
  const role = user.role === 'rep' ? '' : ` (${USER_ROLE_TITLES[user.role] ?? user.role})`;
  return `By ${fullName(user) || 'Unnamed'}${role}`;
}

/** ActivityRow.dateLine: scheduled items "Tomorrow 2:00 PM"; the rest the short date and time they happened. */
export function activityDateLine(
  activity: Pick<ActivityPayload, 'status' | 'scheduledAt' | 'completedAt' | 'createdAt'>,
  now: Date = new Date(),
  locale?: string,
): string {
  const when = activity.status === 'scheduled' ? parseWireDate(activity.scheduledAt) : undefined;
  if (when) return `${formatDayDescription(when, now, locale)} ${formatShortTime(when, locale)}`;
  return formatShortDateTime(effectiveDate(activity), now, locale);
}

/** The status chip on an activity: Cancelled, Overdue or Scheduled (none for completed ones). */
export function activityStatusChip(
  activity: Pick<ActivityPayload, 'status' | 'scheduledAt'>,
  now: Date = new Date(),
): { label: 'Cancelled' | 'Overdue' | 'Scheduled'; color: string } | null {
  if (activity.status === 'cancelled') return { label: 'Cancelled', color: '#8C919E' };
  if (activity.status !== 'scheduled') return null;
  return isOverdue(activity, now) ? { label: 'Overdue', color: '#DB4038' } : { label: 'Scheduled', color: '#2673E6' };
}

/** "From New to Proposal" for stage changes and assignments with values (ActivityRowDetails). */
export function activityTransition(activity: Pick<ActivityPayload, 'type' | 'fromValue' | 'toValue'>): string | null {
  if (activity.type !== 'stageChange' && activity.type !== 'assignment') return null;
  if (activity.fromValue === '' && activity.toValue === '') return null;
  return `From ${activity.fromValue || '—'} to ${activity.toValue || '—'}`;
}

/** "1 attachment" / "3 attachments". */
export function attachmentCountText(n: number): string {
  return `${n} attachment${n === 1 ? '' : 's'}`;
}

export interface VerificationBadge {
  tone: 'verified' | 'unverified' | 'office';
  text: string;
}

/**
 * The visit pill (ActivityRow.VerificationPill): "Verified · 262 ft from record" / "Unverified · 1.2 mi" when the
 * phone measured a distance. A visit with no distance logged from the console says "Logged from the office"
 * (WEB_PLAN.md §5.6): the console has no GPS, so its visits are never verified. `modifiedBy` is the row's last writer
 * (`web-<uuid>` for the console). Other visits without a distance (no GPS fix) show nothing, as on iOS.
 */
export function verificationBadge(
  activity: Pick<ActivityPayload, 'type' | 'distanceFromRecordMeters' | 'verificationRadius'>,
  unit: DistanceUnit,
  modifiedBy?: string,
): VerificationBadge | null {
  if (activity.type !== 'visit') return null;
  const d = activity.distanceFromRecordMeters;
  if (d === undefined || !Number.isFinite(d)) {
    return modifiedBy?.startsWith('web-') ? { tone: 'office', text: 'Logged from the office' } : null;
  }
  const distance = formatShortDistance(d, unit);
  return isVerified(activity)
    ? { tone: 'verified', text: `Verified · ${distance} from record` }
    : { tone: 'unverified', text: `Unverified · ${distance}` };
}

// MARK: - Header

/** "Today" / "Tomorrow" / "Sep 24", with the year only when it is not this year (RecordHeroStats.compactDay). */
export function compactDay(date: Date, now: Date = new Date(), locale?: string): string {
  if (isSameDay(date, now) || isSameDay(date, startOfDayOffset(now, -1)) || isSameDay(date, startOfDayOffset(now, 1))) {
    return formatDayDescription(date, now, locale);
  }
  const sameYear = date.getFullYear() === now.getFullYear();
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' }).format(date);
  }
}

/** The hero's next-activity line: "Overdue Sep 24" (past) or "Next Tomorrow 2:00 PM". */
export function nextScheduledText(next: Date | undefined, now: Date = new Date(), locale?: string): { text: string; overdue: boolean } | null {
  if (!next) return null;
  if (next.getTime() < now.getTime()) return { text: `Overdue ${compactDay(next, now, locale)}`, overdue: true };
  return { text: `Next ${compactDay(next, now, locale)} ${formatShortTime(next, locale)}`, overdue: false };
}

// MARK: - Info tab

/** `Address.line1` / `line2` (street + unit; "City, ST 46320"). */
export function addressLines(a: Pick<AddressPayload, 'street' | 'unit' | 'city' | 'state' | 'postalCode'>): { line1: string; line2: string } {
  const line1 = a.unit === '' ? a.street : `${a.street} ${a.unit}`;
  const cs = [a.city, a.state].filter((s) => s !== '').join(', ');
  const line2 = [cs, a.postalCode].filter((s) => s !== '').join(' ');
  return { line1, line2 };
}

/** `Address.isEmpty`. */
export function isAddressEmpty(a: Pick<AddressPayload, 'street' | 'city' | 'postalCode'>): boolean {
  return a.street === '' && a.city === '' && a.postalCode === '';
}

/** `telURL`: digits and "+" only; null when nothing is left. */
export function telHref(phone: string): string | null {
  const digits = [...phone].filter((c) => '+0123456789'.includes(c)).join('');
  return digits === '' ? null : `tel:${digits}`;
}

/** `mailURL`: needs an "@". */
export function mailHref(email: string): string | null {
  const e = email.trim();
  if (!e.includes('@') || /\s/.test(e)) return null;
  return `mailto:${encodeURI(e)}`;
}

/** `webURL`: http(s) as given, anything else gets "https://"; other schemes (javascript:, data:) are refused. */
export function webHref(site: string): string | null {
  const s = site.trim();
  if (s === '' || /\s/.test(s)) return null;
  const withScheme = /^https?:\/\//i.test(s) ? s : /^[a-z][a-z0-9+.-]*:/i.test(s) ? null : `https://${s}`;
  if (!withScheme) return null;
  try {
    const url = new URL(withScheme);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/** `customFields(for:)`: the fields that apply to a store type, in order. */
export function customFieldsFor<F extends Pick<CustomFieldPayload, 'appliesTo' | 'order'>>(
  fields: readonly F[],
  type: CustomFieldPayload['appliesTo'][number],
): F[] {
  return fields.filter((f) => f.appliesTo.includes(type)).sort((a, b) => a.order - b.order);
}

export type CustomFieldDisplay =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'multiline'; text: string }
  | { kind: 'chips'; items: string[] }
  | { kind: 'link'; text: string; href: string };

/**
 * How a custom field's value reads on the Info tab (CustomFieldValueRow, by field type): money for currency, the
 * medium date, Yes/No, chips for multi-select and links for phone / email / URL. A value whose kind does not match
 * the field (the field's type was changed later) falls back to iOS `displayString`.
 */
export function customFieldDisplay(
  field: Pick<CustomFieldPayload, 'type'>,
  value: FieldValue | undefined,
  currencyCode: string,
  locale?: string,
): CustomFieldDisplay {
  if (!value) return { kind: 'empty' };
  const plain = fieldValueDisplay(value, locale);
  switch (field.type) {
    case 'currency':
      if (value.kind === 'number') return { kind: 'text', text: formatMoney(value.value, currencyCode, locale) };
      break;
    case 'date': {
      const d = fieldValueDate(value);
      if (d) return { kind: 'text', text: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d) };
      break;
    }
    case 'multiSelect':
      if (value.kind === 'options') return value.value.length === 0 ? { kind: 'empty' } : { kind: 'chips', items: [...value.value] };
      break;
    case 'multilineText':
      if (value.kind === 'text') return value.value === '' ? { kind: 'empty' } : { kind: 'multiline', text: value.value };
      break;
    case 'phone':
    case 'email':
    case 'url': {
      if (value.kind !== 'text' || value.value === '') break;
      const href = field.type === 'phone' ? telHref(value.value) : field.type === 'email' ? mailHref(value.value) : webHref(value.value);
      if (href) return { kind: 'link', text: value.value, href };
      break;
    }
    default:
      break;
  }
  return plain === '' ? { kind: 'empty' } : { kind: 'text', text: plain };
}

// MARK: - Files

export interface RecordFiles {
  /** Photos and business cards (the photo grid). */
  photos: DataItem<AttachmentPayload>[];
  /** Documents and audio (the list with Open links). */
  documents: DataItem<AttachmentPayload>[];
}

/** `attachments(for:)`: the store's attachments, newest first, split like RecordFilesTab. */
export function recordFiles(recordId: string, attachments: readonly DataItem<AttachmentPayload>[]): RecordFiles {
  const mine = attachments
    .filter((a) => sameId(a.value.recordId, recordId))
    .sort((a, b) => (parseWireDate(b.value.createdAt)?.getTime() ?? 0) - (parseWireDate(a.value.createdAt)?.getTime() ?? 0));
  return {
    photos: mine.filter((a) => a.value.kind === 'photo' || a.value.kind === 'businessCard'),
    documents: mine.filter((a) => a.value.kind === 'document' || a.value.kind === 'audio'),
  };
}

/** The name an attachment goes by: its caption, else the file name without the iOS UUID prefix, else "Photo". */
export function attachmentTitle(a: Pick<AttachmentPayload, 'caption' | 'fileName' | 'kind'>): string {
  if (a.caption.trim() !== '') return a.caption;
  // Documents are stored as "<UUID>-<original name>.<ext>" (WEB_PLAN.md §3.9); show the original name.
  const name = a.fileName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-?/i, '');
  if (name !== '' && !/^\.[a-z0-9]+$/i.test(name)) return name;
  return a.kind === 'document' ? 'Document' : a.kind === 'audio' ? 'Audio' : a.kind === 'businessCard' ? 'Business card' : 'Photo';
}
